/* ============================================================
   SAM-1072 — New event, four steps (canvas 21 oct, approved): Event · When & where · Contact & access
   · Review, each with the live preview of the public page on the right (Mobile | Desktop).
   - "Copy from English" copies the English text into the empty Spanish fields (it is not a
     translation; "Translate with AI" is a separate decision that needs a backend). It says what it
     did and offers Undo.
   - When & where: first day and last day, one start and one end. Different hours per day wait for
     the backend (an event has a single start and end today).
   The page keeps the form state and the save; this component is the screens.
   ============================================================ */
import React, { useRef, useState } from 'react';
import { TIME_ZONE } from '../../../i18n/format';
import { ArrowLeft, Check, Copy, Eye, Send, Sparkles } from 'lucide-react';
import { S3ImageUpload } from '../../../components/S3ImageUpload';
import { PhoneInput } from '../../../components/ui/PhoneInput';
import { DateField } from '../../../components/ui/DateTimeField';
import { PublicLangProvider } from '../../../i18n/publicEvents';
import { PublicEventDetailView } from '../../public/PublicEventDetail';
import { applyMarkdownEdit, type MarkdownKind } from '../../../utils/markdownEdit';
import { eventDaysEnabled, formToPreviewEvent, missingRequiredFields, type EventForm } from '../eventForm';
import {
  EVENT_STEPS, TIME_OPTIONS, applyWhen, canCopyFromEnglish, copyFirstDayToAll, copyFromEnglish, dayCount, defaultEndTime, englishIsEmpty,
  eventStepBlocker, eventStepIndex, formatTime12, restoreSpanish, reviewRows, spanishIsEmpty, syncDayRows, whenOf, withDayRows,
  type EsSnapshot, type EventStep,
} from './eventWizardModel';

const ink = (n: 900 | 700 | 500) => ({ color: `var(--ink-${n})` });
const brand = 'var(--tenant-primary, #005488)';
const box = 'rounded-[8px] border border-solid border-[var(--line)] bg-[var(--surface)] text-[14px] font-normal';

const Label: React.FC<{ text: string; required?: boolean; hint?: string; error?: string; children: React.ReactNode }> = (
  { text, required, hint, error, children },
) => (
  <label className="flex flex-col gap-1.5 text-[13px] font-semibold min-w-0" style={ink(700)}>
    <span>{text}{required && <span style={{ color: brand }}> *</span>}</span>
    {children}
    {hint && !error && <span className="text-[12px] font-normal" style={ink(500)}>{hint}</span>}
    {error && <span role="alert" className="text-[12px] font-normal" style={{ color: 'var(--danger, #c0392b)' }}>{error}</span>}
  </label>
);

const Card: React.FC<{ title?: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="rounded-[12px] bg-white p-5 sm:p-6 flex flex-col gap-4" style={{ border: '1px solid var(--line)' }}>
    {title && <h2 className="m-0 text-[17px] font-bold" style={ink(900)}>{title}</h2>}
    {children}
  </section>
);

const TimeSelect: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <Label text={label}>
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className={`h-11 px-3 ${box}`}>
      <option value="">Select</option>
      {value && !TIME_OPTIONS.includes(value) && <option value={value}>{formatTime12(value)}</option>}
      {TIME_OPTIONS.map((t) => <option key={t} value={t}>{formatTime12(t)}</option>)}
    </select>
  </Label>
);

// --- Live preview ------------------------------------------------------------------------------

type Device = 'phone' | 'desktop';

/** The public event page as guests see it, from the form; scaled down into the rail. */
const LivePreview: React.FC<{ form: EventForm; isPrivate: boolean }> = ({ form, isPrivate }) => {
  const [device, setDevice] = useState<Device>('phone');
  const phone = device === 'phone';
  const scale = phone ? 0.6 : 0.3;
  const width = phone ? 390 : 1200;
  const height = phone ? 760 : 900;
  // Same pick as capcana.app: the vertical banner on a phone, the wide image elsewhere.
  const heroKey = phone
    ? (form.banner_mobile_url || form.background_img_url)
    : (form.background_img_url || form.banner_mobile_url);
  return (
    <aside className="hidden lg:flex w-[380px] shrink-0 flex-col items-center gap-3 sticky top-6 self-start" aria-label="Live preview">
      <div className="text-[12px] font-bold tracking-wide" style={ink(500)}>LIVE PREVIEW · AS GUESTS SEE IT</div>
      <div role="group" aria-label="Device" className="inline-flex gap-0.5 p-1 rounded-[10px]" style={{ background: 'var(--line-subtle)' }}>
        {(['phone', 'desktop'] as const).map((d) => (
          <button
            key={d}
            type="button"
            aria-pressed={device === d}
            onClick={() => setDevice(d)}
            className="h-8 px-3.5 rounded-[7px] border-0 text-[14px] font-semibold cursor-pointer"
            style={{ background: device === d ? '#fff' : 'transparent', color: device === d ? 'var(--ink-900)' : 'var(--ink-500)', boxShadow: device === d ? '0 1px 3px rgba(0,0,0,.12)' : 'none' }}
          >
            {d === 'phone' ? 'Mobile' : 'Desktop'}
          </button>
        ))}
      </div>
      <div
        data-testid="live-preview-frame"
        className="overflow-hidden bg-white"
        style={{ width: width * scale, height: height * scale, border: phone ? '6px solid #141414' : '1px solid var(--line)', borderRadius: phone ? 28 : 12, boxSizing: 'content-box' }}
      >
        <div style={{ width, height, transform: `scale(${scale})`, transformOrigin: 'top left', pointerEvents: 'none', overflow: 'hidden' }} aria-hidden>
          <PublicLangProvider>
            <PublicEventDetailView
              event={formToPreviewEvent(form, undefined, isPrivate)}
              heroImageKey={heroKey}
              heroFullImage={phone && !!form.banner_mobile_url}
              pageStyle={{ minHeight: '100%' }}
            />
          </PublicLangProvider>
        </div>
      </div>
      <div className="text-[12px] text-center" style={ink(500)}>Preview only. Updates as you type.</div>
    </aside>
  );
};

// --- Step 1 · Event ----------------------------------------------------------------------------

const TOOLS: Array<{ kind: MarkdownKind; label: string; aria: string }> = [
  { kind: 'bold', label: 'B', aria: 'Bold' },
  { kind: 'italic', label: 'I', aria: 'Italic' },
  { kind: 'heading', label: 'H2', aria: 'Heading 2' },
  { kind: 'heading3', label: 'H3', aria: 'Heading 3' },
  { kind: 'bullet', label: '•', aria: 'Bulleted list' },
  { kind: 'numbered', label: '1.', aria: 'Numbered list' },
  { kind: 'link', label: '↗', aria: 'Link' },
];

interface Copied { at: string; before: EsSnapshot }

const EventStepScreen: React.FC<{ form: EventForm; update: <K extends keyof EventForm>(f: K, v: EventForm[K]) => void; setForm: React.Dispatch<React.SetStateAction<EventForm>> }> = (
  { form, update, setForm },
) => {
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const [copied, setCopied] = useState<Copied | null>(null);
  const area = useRef<HTMLTextAreaElement>(null);
  const nameKey = lang === 'en' ? 'name_en' : 'name_es';
  const shortKey = lang === 'en' ? 'short_description_en' : 'short_description_es';
  const descKey = lang === 'en' ? 'description_en' : 'description_es';
  const enDone = !englishIsEmpty(form);
  const esEmpty = spanishIsEmpty(form);
  const noEnglish = englishIsEmpty(form);
  const gaps = canCopyFromEnglish(form);

  const tool = (kind: MarkdownKind) => {
    const el = area.current;
    const value = (form[descKey] as string) || '';
    const edit = applyMarkdownEdit(value, el?.selectionStart ?? value.length, el?.selectionEnd ?? value.length, kind,
      kind === 'link' ? window.prompt('Link URL', 'https://') ?? '' : undefined);
    if (!edit) return;
    update(descKey, edit.value);
    setTimeout(() => { el?.focus(); el?.setSelectionRange(edit.selStart, edit.selEnd); }, 0);
  };

  const doCopy = () => {
    const result = copyFromEnglish(form);
    if (result.filled === 0) return;
    setForm(result.form);
    // "Oct 21, 10:08 PM" on the tenant's clock (Dominican time), whatever the browser's zone.
    const at = new Date().toLocaleString('en-US', { timeZone: TIME_ZONE, month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    setCopied({ at, before: result.before });
  };
  const undo = () => {
    if (!copied) return;
    setForm((prev) => restoreSpanish(prev, copied.before));
    setCopied(null);
  };
  /** Writing in Spanish by hand ends the "copied, translate it" state. */
  const editEs = <K extends 'name_es' | 'short_description_es' | 'description_es'>(k: K, v: string) => {
    update(k, v);
    setCopied(null);
  };
  const edit = (k: typeof nameKey | typeof shortKey | typeof descKey, v: string) => (lang === 'es' ? editEs(k as 'name_es', v) : update(k, v));

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="m-0 text-[24px] font-bold" style={ink(900)}>What is the event?</h1>
        <p className="m-0 mt-1.5 text-[15px]" style={ink(500)}>Name, description and pictures. Pictures are optional until you publish.</p>
      </div>
      <Card title="Event information">
        <div className="flex" role="tablist" aria-label="Language" style={{ borderBottom: '1px solid var(--line)' }}>
          {([['en', 'English'], ['es', 'Español']] as const).map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={lang === k}
              onClick={() => setLang(k)}
              className="h-[46px] px-4 inline-flex items-center gap-2 bg-transparent border-0 text-[15px] font-bold cursor-pointer"
              style={{ color: lang === k ? brand : 'var(--ink-500)', borderBottom: `3px solid ${lang === k ? brand : 'transparent'}` }}
            >
              {label}
              {k === 'en' && enDone && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--ok-bg)', color: 'var(--ok-fg)' }}>Done</span>}
              {k === 'es' && copied && <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full" style={{ background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>Copied · translate</span>}
            </button>
          ))}
        </div>

        {lang === 'es' && (esEmpty || gaps) && (
          noEnglish ? (
            <div className="flex flex-col gap-1 px-3.5 py-2.5 rounded-[10px] text-[13px]" style={{ background: 'var(--surface)', border: '1px solid var(--line)' }}>
              <b style={ink(900)}>Write the English version first</b>
              <span style={ink(500)}>There is nothing to copy yet.</span>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap px-3.5 py-3 rounded-[10px]" style={{ background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>
              <span className="flex items-start gap-2.5 text-[13px]">
                <Sparkles size={16} aria-hidden className="shrink-0 mt-0.5" />
                <span><b>{esEmpty ? 'Español is empty' : 'Some Spanish fields are empty'}</b><br />Copy the English text into the empty Spanish fields, then translate it by hand. Fields you already wrote are kept.</span>
              </span>
              <button type="button" onClick={doCopy} className="btn shrink-0"><Copy size={14} /> Copy from English</button>
            </div>
          )
        )}
        {lang === 'es' && copied && (
          <div className="flex items-center gap-2 text-[13px]" style={ink(500)}>
            <Sparkles size={14} aria-hidden style={{ color: brand }} />
            <span>Copied from English · {copied.at}</span>
            <span aria-hidden>·</span>
            <button type="button" onClick={undo} className="bg-transparent border-0 p-0 font-semibold cursor-pointer" style={{ color: brand }}>Undo</button>
          </div>
        )}

        <Label text="Name" required>
          <input
            aria-label="Name"
            value={(form[nameKey] as string) || ''}
            onChange={(e) => edit(nameKey, e.target.value)}
            placeholder={lang === 'en' ? 'Event name' : 'Nombre del evento'}
            className={`h-11 px-3 ${box}`}
          />
        </Label>
        <Label text="Short description">
          <input
            aria-label="Short description"
            value={(form[shortKey] as string) || ''}
            onChange={(e) => edit(shortKey, e.target.value)}
            placeholder={lang === 'en' ? 'Brief description' : 'Descripción breve'}
            className={`h-11 px-3 ${box}`}
          />
        </Label>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2 text-[13px] font-semibold" style={ink(700)}>
            <span>Description</span>
            <span className="ml-auto text-[12px] font-normal" style={ink(500)}>Markdown: <code>##</code> heading · <code>**bold**</code> · <code>-</code> list</span>
          </div>
          <div className="flex gap-1.5 flex-wrap" role="toolbar" aria-label="Description formatting">
            {TOOLS.map((t) => (
              <button
                key={t.kind}
                type="button"
                aria-label={t.aria}
                onClick={() => tool(t.kind)}
                className="min-w-[34px] h-[30px] px-1.5 rounded-[6px] bg-white text-[13px] font-bold cursor-pointer"
                style={{ border: '1px solid var(--line)', ...ink(900) }}
              >
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            ref={area}
            aria-label="Description"
            value={(form[descKey] as string) || ''}
            onChange={(e) => edit(descKey, e.target.value)}
            placeholder={lang === 'en' ? 'Full description' : 'Descripción completa'}
            className={`p-3.5 leading-relaxed font-mono text-[13px] ${box}`}
            style={{ minHeight: 320 }}
          />
        </div>
      </Card>

      <Card title="Images">
        <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
          <S3ImageUpload variant="slot" label="Logo" slotWidth="140px" slotHeight="130px" placeholder="Logo" value={form.logo_url || ''} onChange={(k) => update('logo_url', k)} />
          <S3ImageUpload variant="slot" label="Web banner" slotHeight="130px" placeholder="Web banner · 1920×600" value={form.background_img_url || ''} onChange={(k) => update('background_img_url', k)} />
        </div>
        <S3ImageUpload variant="slot" label="Mobile banner" slotHeight="130px" placeholder="Mobile banner · 1080×1350" value={form.banner_mobile_url || ''} onChange={(k) => update('banner_mobile_url', k)} />
      </Card>
    </div>
  );
};

// --- Step 2 · When & where ---------------------------------------------------------------------

const dayLabel = (iso: string): string => new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

const WhenStep: React.FC<{ form: EventForm; setForm: React.Dispatch<React.SetStateAction<EventForm>>; update: <K extends keyof EventForm>(f: K, v: EventForm[K]) => void }> = (
  { form, setForm, update },
) => {
  const w = whenOf(form);
  const days = dayCount(w.firstDay, w.lastDay);
  // Ends follows Starts (same length) until it is set by hand.
  const endTouched = useRef(!!w.startTime && !!w.endTime && w.endTime !== defaultEndTime(w.startTime));
  const perDay = eventDaysEnabled() && days > 1;
  const set = (next: Parameters<typeof applyWhen>[1]) => {
    if (next.endTime !== undefined) endTouched.current = true;
    setForm((prev) => {
      const moved = applyWhen(prev, next, { endTouched: endTouched.current });
      return eventDaysEnabled() ? withDayRows(moved, syncDayRows(moved.days, whenOf(moved))) : moved;
    });
  };
  const rows = form.days ?? [];
  const setRow = (date: string, patch: Partial<{ start: string; end: string; closed: boolean }>) =>
    setForm((prev) => withDayRows(prev, (prev.days ?? []).map((r) => (r.date === date ? { ...r, ...patch } : r))));
  const multi = days > 1;
  return (
    <div className="flex flex-col gap-5">
      <h1 className="m-0 text-[24px] font-bold" style={ink(900)}>When and where?</h1>
      <Card>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Label text="First day" required>
            <DateField ariaLabel="First day" value={w.firstDay} onChange={(v) => set({ firstDay: v })} />
          </Label>
          <Label text="Last day" required>
            <DateField ariaLabel="Last day" value={w.lastDay} min={w.firstDay || undefined} onChange={(v) => set({ lastDay: v })} />
          </Label>
        </div>
        {days > 0 && (
          <div className="flex gap-2.5 items-center flex-wrap">
            <span className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full" style={{ background: `color-mix(in srgb, ${brand} 10%, white)`, color: brand }}>
              {days} {days === 1 ? 'day' : 'days'}
            </span>
            <span className="text-[12px]" style={ink(500)}>
              {perDay
                ? 'Each day gets its own start and end. Guests see them on the event page. Close a day to skip it.'
                : multi ? 'The event starts at the first time on the first day and ends at the second time on the last day.' : 'One day: a single start and end.'}
            </span>
          </div>
        )}
        {!perDay && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TimeSelect label={multi ? 'Starts (first day)' : 'Starts'} value={w.startTime} onChange={(v) => set({ startTime: v })} />
            <TimeSelect label={multi ? 'Ends (last day)' : 'Ends'} value={w.endTime} onChange={(v) => set({ endTime: v })} />
          </div>
        )}
        {perDay && (
          <div className="flex flex-col gap-2.5">
            <div className="flex items-center justify-between">
              <div className="text-[15px] font-bold" style={ink(900)}>Hours per day</div>
              <button type="button" onClick={() => setForm((prev) => withDayRows(prev, copyFirstDayToAll(prev.days ?? [])))} className="bg-transparent border-0 p-0 text-[13px] font-semibold cursor-pointer" style={{ color: brand }}>
                Copy first day to all
              </button>
            </div>
            {rows.map((r) => (
              <div key={r.date} className="grid grid-cols-[1fr_1fr] sm:grid-cols-[130px_1fr_1fr_80px] gap-2.5 items-center py-2" style={{ borderTop: '1px solid var(--line-subtle)', opacity: r.closed ? 0.5 : 1 }}>
                <span className="text-[14px] font-semibold col-span-2 sm:col-span-1" style={ink(900)}>{dayLabel(r.date)}</span>
                <select aria-label={`Starts ${r.date}`} disabled={r.closed} value={r.start} onChange={(e) => setRow(r.date, { start: e.target.value })} className={`h-11 px-3 ${box}`}>
                  {TIME_OPTIONS.map((t) => <option key={t} value={t}>{formatTime12(t)}</option>)}
                </select>
                <select aria-label={`Ends ${r.date}`} disabled={r.closed} value={r.end} onChange={(e) => setRow(r.date, { end: e.target.value })} className={`h-11 px-3 ${box}`}>
                  {TIME_OPTIONS.map((t) => <option key={t} value={t}>{formatTime12(t)}</option>)}
                </select>
                <button type="button" onClick={() => setRow(r.date, { closed: !r.closed })} className="bg-transparent border-0 p-0 text-[13px] font-semibold cursor-pointer text-left" style={{ color: r.closed ? brand : 'var(--bad-fg)' }}>
                  {r.closed ? 'Reopen' : 'Close day'}
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card>
        <Label text="Address">
          <input aria-label="Address" value={form.address || ''} onChange={(e) => update('address', e.target.value)} className={`h-11 px-3 ${box}`} />
        </Label>
      </Card>
    </div>
  );
};

// --- Step 3 · Contact & access -----------------------------------------------------------------

interface Option { id: string | number; label: string }

const ContactStep: React.FC<{
  form: EventForm;
  update: <K extends keyof EventForm>(f: K, v: EventForm[K]) => void;
  businesses: Array<{ id: string; name: string }>;
  eventTypes: Option[];
  eventGroups: Option[];
  accessOptions: Option[];
  isPrivate: boolean;
}> = ({ form, update, businesses, eventTypes, eventGroups, accessOptions, isPrivate }) => (
  <div className="flex flex-col gap-5">
    <h1 className="m-0 text-[24px] font-bold" style={ink(900)}>Contact and access</h1>
    <Card title="Contact">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
        <div className="flex flex-col gap-1.5 text-[13px] font-semibold" style={ink(700)}>
          <span>Phone number</span>
          <PhoneInput value={form.phone_number || ''} onChange={(v) => update('phone_number', v)} />
        </div>
        <Label text="Instagram">
          <input aria-label="Instagram" value={form.instagram_url || ''} onChange={(e) => update('instagram_url', e.target.value)} placeholder="@handle or URL" className={`h-11 px-3 ${box}`} />
        </Label>
      </div>
      <Label text="Website">
        <input aria-label="Website" value={form.website_url || ''} onChange={(e) => update('website_url', e.target.value)} placeholder="https://" className={`h-11 px-3 ${box}`} />
      </Label>
    </Card>
    <Card title="Access">
      <Label text="Business" required>
        <select aria-label="Business" value={form.business_id || ''} onChange={(e) => update('business_id', e.target.value)} className={`h-11 px-3 ${box}`}>
          <option value="">Select a business…</option>
          {businesses.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Label text="Event type">
          <select aria-label="Event type" value={form.event_type_id != null ? String(form.event_type_id) : ''} onChange={(e) => update('event_type_id', e.target.value ? Number(e.target.value) : undefined)} className={`h-11 px-3 ${box}`}>
            <option value="">No type</option>
            {eventTypes.map((t) => <option key={t.id} value={String(t.id)}>{t.label}</option>)}
          </select>
        </Label>
        <Label text="Event group">
          <select aria-label="Event group" value={form.event_group_id != null ? String(form.event_group_id) : ''} onChange={(e) => update('event_group_id', e.target.value ? Number(e.target.value) : undefined)} className={`h-11 px-3 ${box}`}>
            <option value="">No group</option>
            {eventGroups.map((g) => <option key={g.id} value={String(g.id)}>{g.label}</option>)}
          </select>
        </Label>
      </div>
      <Label text="Access settings" hint="Companion rules: adults and children limits">
        <select aria-label="Access settings" value={form.access_settings_id != null ? String(form.access_settings_id) : ''} onChange={(e) => update('access_settings_id', e.target.value ? Number(e.target.value) : undefined)} className={`h-11 px-3 ${box}`}>
          <option value="">None</option>
          {accessOptions.map((a) => <option key={a.id} value={String(a.id)}>{a.label}</option>)}
        </select>
      </Label>
      <Label text="Capacity" hint="Maximum guests across all passes.">
        <span className={`flex items-center gap-2 h-11 px-3 ${box}`}>
          <input
            inputMode="numeric"
            aria-label="Capacity"
            value={form.capacity ? String(form.capacity) : ''}
            onChange={(e) => update('capacity', parseInt(e.target.value.replace(/[^\d]/g, ''), 10) || 0)}
            className="grow min-w-0 bg-transparent border-0 outline-none text-[14px]"
          />
          <span className="shrink-0 text-[14px] font-medium" style={ink(500)}>people</span>
        </span>
      </Label>
      {isPrivate && <span className="text-[12px]" style={ink(500)}>The event code is generated when you save the event.</span>}
    </Card>
  </div>
);

// --- Step 4 · Review ---------------------------------------------------------------------------

const ReviewStep: React.FC<{
  form: EventForm; eventTypeName?: string; accessLabel?: string; isEventApprover: boolean; go: (s: EventStep) => void; onPreview: () => void;
}> = ({ form, eventTypeName, accessLabel, isEventApprover, go, onPreview }) => (
  <div className="flex flex-col gap-5">
    <div>
      <h1 className="m-0 text-[24px] font-bold" style={ink(900)}>Ready to publish?</h1>
      <p className="m-0 mt-1.5 text-[15px]" style={ink(500)}>Everything in one place. Publishing needs a name and dates; the rest is optional.</p>
    </div>
    <Card>
      <dl className="m-0 flex flex-col">
        {reviewRows(form, { eventTypeName, accessLabel }).map((r) => (
          <div key={r.label} className="flex items-start gap-3 py-3 first:pt-0" style={{ borderTop: '1px solid var(--line-subtle)' }}>
            <dt className="w-24 shrink-0 text-[13px] font-semibold" style={ink(500)}>{r.label}</dt>
            <dd className="m-0 grow min-w-0 text-[14px] break-words" style={{ color: r.warn ? 'var(--warn-fg)' : 'var(--ink-900)' }}>{r.value}</dd>
            <button type="button" aria-label={`Edit ${r.label}`} onClick={() => go(r.step)} className="bg-transparent border-0 p-0 text-[13px] font-semibold cursor-pointer" style={{ color: brand }}>Edit</button>
          </div>
        ))}
      </dl>
    </Card>
    <Card title="Status">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <span className="text-[12px] font-semibold px-2.5 py-1 rounded-full" style={{ background: 'var(--warn-bg)', color: 'var(--warn-fg)' }}>Draft</span>
          <span className="text-[13px]" style={ink(500)}>
            {isEventApprover
              ? 'A draft is not visible on capcana.app. Check it in Preview, then publish.'
              : 'Saved as a draft. A Cap Cana admin reviews and publishes it.'}
          </span>
        </div>
        <button type="button" className="btn ghost" onClick={onPreview}><Eye size={14} /> Preview</button>
      </div>
    </Card>
  </div>
);

// --- Shell -------------------------------------------------------------------------------------

export const NewEventWizard: React.FC<{
  form: EventForm;
  setForm: React.Dispatch<React.SetStateAction<EventForm>>;
  initialStep?: EventStep;
  businesses: Array<{ id: string; name: string }>;
  eventTypes: Option[];
  eventGroups: Option[];
  accessOptions: Option[];
  isPrivate: boolean;
  isEventApprover: boolean;
  isSaving: boolean;
  onSave: (status: 'draft' | 'published') => void;
  onPreview: () => void;
  onCancel: () => void;
}> = ({ form, setForm, initialStep = 'event', businesses, eventTypes, eventGroups, accessOptions, isPrivate, isEventApprover, isSaving, onSave, onPreview, onCancel }) => {
  const [step, setStep] = useState<EventStep>(initialStep);
  const idx = eventStepIndex(step);
  const update = <K extends keyof EventForm>(field: K, value: EventForm[K]) => setForm((prev) => ({ ...prev, [field]: value }));
  const blocker = eventStepBlocker(step, form);
  const missing = missingRequiredFields(form);
  const last = step === 'review';
  const name = (form.name_en || form.name_es || '').trim();
  const title = `New event${name ? ` · ${name}` : ''}`;
  const go = (s: EventStep) => setStep(s);
  const back = () => (idx === 0 ? onCancel() : setStep(EVENT_STEPS[idx - 1].key));
  const next = () => { if (idx < EVENT_STEPS.length - 1) setStep(EVENT_STEPS[idx + 1].key); };
  const typeName = eventTypes.find((t) => String(t.id) === String(form.event_type_id))?.label;
  const accessLabel = accessOptions.find((a) => String(a.id) === String(form.access_settings_id))?.label;

  return (
    <div className="flex flex-col min-h-full" style={{ background: 'var(--surface)' }}>
      <header className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3 bg-white flex-wrap" style={{ borderBottom: '1px solid var(--line)' }}>
        <div className="flex items-center gap-3.5 min-w-0">
          <button type="button" aria-label="Back to Events" onClick={onCancel} className="w-10 h-10 shrink-0 rounded-[10px] bg-white flex items-center justify-center cursor-pointer" style={{ border: '1px solid var(--line)', ...ink(900) }}>
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <div className="text-[18px] sm:text-[20px] font-bold truncate" style={ink(900)}>{title}</div>
            <div className="text-[13px]" style={ink(500)}>Step {idx + 1} of {EVENT_STEPS.length} · {EVENT_STEPS[idx].title}</div>
          </div>
        </div>
        <ol aria-label="Steps" className="hidden md:flex gap-2 list-none m-0 p-0">
          {EVENT_STEPS.map((s, i) => {
            const done = i < idx;
            const on = i === idx;
            return (
              <li key={s.key} aria-current={on ? 'step' : undefined}>
                {/* A finished step is a button back to it; the ones ahead are reached with Continue. */}
                <button
                  type="button"
                  disabled={!done}
                  onClick={() => go(s.key)}
                  className="flex items-center gap-2 h-10 px-2 text-[13px] font-semibold bg-transparent border-0"
                  style={{ color: on ? 'var(--ink-900)' : done ? brand : 'var(--ink-500)', cursor: done ? 'pointer' : 'default', textDecoration: done ? 'underline' : 'none' }}
                >
                <span aria-hidden className="inline-flex items-center justify-center rounded-full text-[12px]" style={{ width: 24, height: 24, background: on ? brand : done ? `color-mix(in srgb, ${brand} 10%, white)` : 'var(--line-subtle)', color: on ? '#fff' : done ? brand : 'var(--ink-500)' }}>
                  {done ? <Check size={12} strokeWidth={3} /> : i + 1}
                </span>
                {s.label}
                </button>
              </li>
            );
          })}
        </ol>
        <button type="button" onClick={onCancel} className="btn ghost shrink-0">Cancel</button>
      </header>

      <main className="grow flex justify-center gap-8 px-4 sm:px-6 pt-6 sm:pt-8 pb-12">
        <div className="w-full max-w-[720px] min-w-0">
          {step === 'event' && <EventStepScreen form={form} update={update} setForm={setForm} />}
          {step === 'when' && <WhenStep form={form} setForm={setForm} update={update} />}
          {step === 'contact' && <ContactStep form={form} update={update} businesses={businesses} eventTypes={eventTypes} eventGroups={eventGroups} accessOptions={accessOptions} isPrivate={isPrivate} />}
          {step === 'review' && <ReviewStep form={form} eventTypeName={typeName} accessLabel={accessLabel} isEventApprover={isEventApprover} go={go} onPreview={onPreview} />}
        </div>
        <LivePreview form={form} isPrivate={isPrivate} />
      </main>

      <footer className="sticky bottom-0 mt-auto flex items-center justify-between gap-3 px-4 sm:px-6 py-3.5 bg-white flex-wrap" style={{ borderTop: '1px solid var(--line)' }}>
        <div>{idx > 0 && <button type="button" className="btn" onClick={back} disabled={isSaving}>Back</button>}</div>
        <div className="flex items-center gap-3 flex-wrap justify-end">
          <button type="button" className="btn ghost lg:hidden" onClick={onPreview}><Eye size={14} /> Preview</button>
          {!last && blocker && <span className="text-xs" style={ink(500)}>{blocker}</span>}
          {last && missing.length > 0 && <span className="text-xs" style={ink(500)}>Fill in {missing.map((m) => m.label).join(', ')} to save.</span>}
          {last ? (
            <>
              <button type="button" className={isEventApprover ? 'btn' : 'btn primary'} onClick={() => onSave('draft')} disabled={missing.length > 0 || isSaving}>
                {isSaving ? 'Saving…' : 'Save draft'}
              </button>
              {isEventApprover && (
                <button type="button" className="btn primary" onClick={() => onSave('published')} disabled={missing.length > 0 || isSaving}>
                  <Send size={14} /> Publish
                </button>
              )}
            </>
          ) : (
            <button type="button" className="btn primary" onClick={next} disabled={!!blocker}>Continue</button>
          )}
        </div>
      </footer>
    </div>
  );
};
